import { useState, useRef, useEffect, useCallback } from 'react';
import { Search, ChevronDown, X, Plus } from 'lucide-react';
import clsx from 'clsx';

export interface SearchableSelectOption {
  value: string;
  label: string;
  subtitle?: string; // Optional secondary text (e.g., address, contact)
}

export interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  emptyMessage?: string;
  /** Allow clearing the selection */
  clearable?: boolean;
  /** Icon to show on the left side */
  icon?: React.ReactNode;
  /** Compact mode for filter bars */
  compact?: boolean;
  /** Allow creating new options when no match is found */
  creatable?: boolean;
  /** Callback when user selects the "create" option */
  onCreateOption?: (label: string) => void;
}

export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'חפש...',
  label,
  error,
  required,
  disabled,
  className,
  emptyMessage = 'לא נמצאו תוצאות',
  clearable = false,
  icon,
  compact = false,
  creatable = false,
  onCreateOption,
}: SearchableSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Get selected option
  const selectedOption = options.find((opt) => opt.value === value);

  // Filter options based on search query
  const filteredOptions = options.filter(
    (option) =>
      option.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
      option.subtitle?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Show create option when creatable, query is non-empty, and no exact label match
  const showCreateOption =
    creatable &&
    searchQuery.trim().length > 0 &&
    !options.some((opt) => opt.label.trim().toLowerCase() === searchQuery.trim().toLowerCase());
  const effectiveOptionsCount = filteredOptions.length + (showCreateOption ? 1 : 0);

  // Handler to update search query and reset highlighted index
  const handleSearchChange = useCallback((newQuery: string) => {
    setSearchQuery(newQuery);
    setHighlightedIndex(0);
  }, []);

  // Scroll highlighted option into view
  useEffect(() => {
    if (isOpen && listRef.current) {
      const highlightedElement = listRef.current.children[highlightedIndex] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex, isOpen]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleOpen = useCallback(() => {
    if (!disabled) {
      setIsOpen(true);
      setSearchQuery('');
      // Focus input after state update
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [disabled]);

  const handleSelect = useCallback(
    (optionValue: string) => {
      onChange(optionValue);
      setIsOpen(false);
      setSearchQuery('');
    },
    [onChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isOpen) {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
          e.preventDefault();
          handleOpen();
        }
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex((prev) => (prev < effectiveOptionsCount - 1 ? prev + 1 : prev));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (highlightedIndex === filteredOptions.length && showCreateOption) {
            onCreateOption?.(searchQuery.trim());
            setIsOpen(false);
            setSearchQuery('');
          } else if (filteredOptions[highlightedIndex]) {
            handleSelect(filteredOptions[highlightedIndex].value);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setIsOpen(false);
          setSearchQuery('');
          break;
        case 'Tab':
          setIsOpen(false);
          setSearchQuery('');
          break;
      }
    },
    [
      isOpen,
      filteredOptions,
      highlightedIndex,
      handleOpen,
      handleSelect,
      effectiveOptionsCount,
      showCreateOption,
      onCreateOption,
      searchQuery,
    ]
  );

  return (
    <div className={clsx('w-full', className)} ref={containerRef}>
      {label && (
        <label className="mb-1 block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="mr-1 text-red-500">*</span>}
        </label>
      )}

      <div className="relative">
        {/* Trigger / Display */}
        {!isOpen ? (
          <button
            type="button"
            onClick={handleOpen}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            className={clsx(
              'flex w-full items-center text-right',
              compact ? 'gap-2 px-2.5 py-1.5 text-sm' : 'gap-3 px-3 py-2.5 md:py-2',
              compact ? 'rounded border bg-transparent' : 'rounded-lg border bg-white shadow-sm',
              'transition-[border-color,background-color,box-shadow] duration-200',
              compact ? 'hover:border-[#3C5FA8] hover:bg-white' : 'hover:border-[#3C5FA8]/50',
              compact
                ? 'focus:border-[#3C5FA8] focus:bg-white focus:ring-1 focus:ring-[#3C5FA8] focus:outline-none'
                : 'focus:border-transparent focus:bg-white focus:ring-2 focus:ring-[#3C5FA8] focus:outline-none',
              'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-gray-200',
              error ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
            )}
          >
            {icon && !compact && <span className="flex-shrink-0 text-gray-400">{icon}</span>}

            <span
              className={clsx(
                'flex-1 truncate',
                selectedOption ? 'text-gray-900' : 'text-gray-400'
              )}
            >
              {selectedOption?.label || placeholder}
            </span>

            <div className="flex items-center gap-1">
              {clearable && selectedOption && (
                <span
                  role="button"
                  tabIndex={-1}
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onChange('');
                    setSearchQuery('');
                  }}
                  aria-label="נקה בחירה"
                  className="cursor-pointer rounded-full p-0.5 transition-colors hover:bg-gray-200"
                >
                  <X size={compact ? 12 : 14} className="text-gray-400" />
                </span>
              )}
              <ChevronDown
                size={compact ? 14 : 18}
                className={clsx(
                  'text-gray-400 transition-transform duration-200',
                  isOpen && 'rotate-180'
                )}
              />
            </div>
          </button>
        ) : (
          // Search Input (shown when open)
          <div
            className={clsx(
              'flex w-full items-center gap-2',
              compact ? 'px-2.5 py-1.5' : 'px-3 py-2.5 md:py-2',
              'rounded-none border-x-0 border-t-0 border-b-2 border-b-[#3C5FA8] bg-transparent'
            )}
          >
            <Search size={compact ? 14 : 18} className="flex-shrink-0 text-[#3C5FA8]" />
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              className={clsx(
                'flex-1 bg-transparent text-gray-900 outline-none placeholder:text-gray-400',
                compact && 'text-sm'
              )}
              autoComplete="off"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => handleSearchChange('')}
                aria-label="נקה חיפוש"
                className="relative rounded-full p-1 transition-colors before:absolute before:top-1/2 before:left-1/2 before:size-10 before:-translate-x-1/2 before:-translate-y-1/2 before:content-[''] hover:bg-gray-100"
              >
                <X size={14} className="text-gray-400" />
              </button>
            )}
          </div>
        )}

        {/* Dropdown */}
        {isOpen && (
          <ul
            ref={listRef}
            className={clsx(
              'absolute z-50 mt-2 w-full',
              compact ? 'py-1' : 'py-2',
              compact
                ? 'rounded-lg bg-white shadow-[0_1px_2px_rgba(0,0,0,0.06),0_8px_24px_rgba(0,0,0,0.10)]'
                : 'rounded-xl bg-white shadow-[0_1px_3px_rgba(0,0,0,0.07),0_12px_32px_rgba(0,0,0,0.12)]',
              'max-h-64 overflow-y-auto',
              'animate-in fade-in slide-in-from-top-2 duration-200'
            )}
          >
            {filteredOptions.length === 0 && !showCreateOption ? (
              <li
                className={clsx(
                  'text-center text-gray-500',
                  compact ? 'px-3 py-2 text-sm' : 'px-4 py-3'
                )}
              >
                {emptyMessage}
              </li>
            ) : (
              <>
                {filteredOptions.map((option, index) => (
                  <li
                    key={option.value}
                    onClick={() => handleSelect(option.value)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    className={clsx(
                      'cursor-pointer transition-colors',
                      compact ? 'px-3 py-2' : 'px-4 py-3',
                      index === highlightedIndex && 'bg-[#3C5FA8]/8',
                      option.value === value && 'bg-[#3C5FA8]/12',
                      option.value !== value && index !== highlightedIndex && 'hover:bg-gray-50'
                    )}
                  >
                    <div className={clsx('font-medium text-gray-900', compact && 'text-sm')}>
                      {option.label}
                    </div>
                    {option.subtitle && (
                      <div
                        className={clsx(
                          'mt-0.5 truncate text-gray-500',
                          compact ? 'text-xs' : 'text-sm'
                        )}
                      >
                        {option.subtitle}
                      </div>
                    )}
                  </li>
                ))}
                {showCreateOption && (
                  <li
                    onClick={() => {
                      onCreateOption?.(searchQuery.trim());
                      setIsOpen(false);
                      setSearchQuery('');
                    }}
                    onMouseEnter={() => setHighlightedIndex(filteredOptions.length)}
                    className={clsx(
                      'cursor-pointer border-t border-gray-100 transition-colors',
                      compact ? 'px-3 py-2' : 'px-4 py-3',
                      highlightedIndex === filteredOptions.length && 'bg-emerald-50'
                    )}
                  >
                    <div
                      className={clsx(
                        'flex items-center gap-1.5 font-medium text-emerald-700',
                        compact && 'text-sm'
                      )}
                    >
                      <Plus size={14} />
                      צור: {searchQuery.trim()}
                    </div>
                  </li>
                )}
              </>
            )}
          </ul>
        )}
      </div>

      {error && <p className="mt-1.5 text-sm text-red-600">{error}</p>}
    </div>
  );
}
